import { useEffect } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  Button,
  CheckIcon,
  Dialog,
  Flex,
  Image,
  List,
  Text,
  UpgradeDialog,
  useBreakpoint,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import { FLOW_TRACKING } from './flows'
import type { Flow } from './flows'

/**
 * Each Insights upgrade flow brings its own heading / body / illustration
 * but shares the same shell, bullets, and CTA. Adding a new flow = adding
 * an entry here; callers pass the flow name only.
 */
const FLOW_COPY: Record<
  Flow,
  { title: string; description: string; imageAlt: string; imageSrc: string }
> = {
  'date-range': {
    title: 'Unlock insights across date ranges and more',
    description:
      "Upgrade to unlock flexible date ranges up to a year. You'll also get:",
    imageAlt: 'Upgrade to unlock custom date ranges',
    imageSrc:
      'https://buffer-publish.s3.amazonaws.com/images/welcome-essentials.png',
  },
  // Placeholder illustration from Figma node 887:1036 (CORE-4965). Designer
  // will replace at the same S3 key once the final lands — no code change
  // needed.
  export: {
    title: 'Take Your Data Anywhere',
    description:
      "Export your post performance as a CSV for spreadsheets and reports, or as Markdown to drop straight into your favorite AI tool. You'll also get:",
    imageAlt: 'Take your insights anywhere — export to CSV or Markdown',
    imageSrc:
      'https://buffer-publish.s3.amazonaws.com/images/insights-export-upgrade.png',
  },
}

interface InsightsUpgradeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpgradeClick: () => void
  trigger?: ReactNode
  /** Which upgrade flow this dialog is messaging. Defaults to `date-range`. */
  flow?: Flow
}

export function InsightsUpgradeDialog({
  open,
  onOpenChange,
  onUpgradeClick,
  trigger,
  flow = 'date-range',
}: InsightsUpgradeDialogProps): JSX.Element {
  const breakpoint = useBreakpoint()
  const isSmallScreen =
    breakpoint === 'initial' || breakpoint === 'xs' || breakpoint === 'sm'
  const { title, description, imageAlt, imageSrc } = FLOW_COPY[flow]

  const { cta, upgradePathName } = FLOW_TRACKING[flow]
  const commonTrackingProperties = useCommonTrackingProperties()

  // Fire the `Upgrade Path Viewed` impression on open. Keyed on `open` (not
  // `onOpenChange`) because callers control `open` as a prop, and Radix only
  // invokes `onOpenChange` for internal events (escape, outside click, trigger)
  // — so a callback would miss programmatic opens and re-opens.
  useEffect(() => {
    if (!open || !commonTrackingProperties.organizationId) return

    BufferTracker.upgradePathViewed({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      cta,
      upgradePathName,
      ...commonTrackingProperties,
    })
  }, [open, cta, upgradePathName, commonTrackingProperties])

  if (isSmallScreen) {
    return (
      <>
        {trigger}
        <Dialog open={open} onOpenChange={onOpenChange}>
          <Dialog.Content>
            <Flex justify="center" direction="column" align="center" gap="lg">
              <Dialog.Header>
                <Flex justify="center">
                  <Image src={imageSrc} alt={imageAlt} />
                </Flex>
              </Dialog.Header>
              <Dialog.Body>
                <Flex direction="column" gap="xs">
                  <Dialog.Title>{title}</Dialog.Title>
                  <Dialog.Description color="default">
                    {description}
                  </Dialog.Description>
                  <List>
                    <List.Item icon={CheckIcon}>
                      <Text color="default">
                        Advanced analytics with extended history.
                      </Text>
                    </List.Item>
                    <List.Item icon={CheckIcon}>
                      <Text color="default">
                        Unlimited post scheduling, threads, ideas and tags.
                      </Text>
                    </List.Item>
                    <List.Item icon={CheckIcon}>
                      <Text color="default">
                        Speed up posting with saved hashtags.
                      </Text>
                    </List.Item>
                  </List>
                </Flex>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="primary" size="large" onClick={onUpgradeClick}>
                  See Upgrade Options
                </Button>
              </Dialog.Footer>
            </Flex>
          </Dialog.Content>
        </Dialog>
      </>
    )
  }

  return (
    <UpgradeDialog open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <UpgradeDialog.Trigger asChild>{trigger}</UpgradeDialog.Trigger>
      )}
      <UpgradeDialog.Content>
        <UpgradeDialog.TextContent>
          <UpgradeDialog.Title>{title}</UpgradeDialog.Title>
          <UpgradeDialog.Description size="md" color="default">
            {description}
          </UpgradeDialog.Description>
          <List>
            <List.Item icon={CheckIcon}>
              <Text color="default">
                Advanced analytics with extended history.
              </Text>
            </List.Item>
            <List.Item icon={CheckIcon}>
              <Text color="default">
                Unlimited post scheduling, threads, ideas and tags.
              </Text>
            </List.Item>
            <List.Item icon={CheckIcon}>
              <Text color="default">Speed up posting with saved hashtags.</Text>
            </List.Item>
          </List>
          <UpgradeDialog.Actions>
            <Button variant="primary" size="large" onClick={onUpgradeClick}>
              See Upgrade Options
            </Button>
          </UpgradeDialog.Actions>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image src={imageSrc} alt={imageAlt} />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
